import { API_BASE } from './constants.js'
import { publishAblyEvent } from '../hooks/useAblyChannel.js'

/**
 * Client for the shared meta store (comments, weekly summaries, reactions,
 * DMs, presence, action items, etc.).
 *
 * Endpoints (same Cloudflare Worker as /goals):
 *   GET  /meta  — returns the meta object
 *   POST /meta  — saves the full meta object
 *
 * Meta object shape (from q2-goals.html line 2846–2858):
 *   {
 *     fieldEdits:       {},  // "p0-g1-current" → { by, at }
 *     comments:         [],  // threads + replies
 *     weeklySummary:    {},  // "p0-g1" → { tldr, wins, flags, savedAt, savedBy }
 *     execSummary:      null,
 *     aiPreferences:    [],
 *     hsComments:       [],  // owned by hubspot-dashboard, preserved on save
 *     reactions:        {},  // "p0-g1" → { emoji: [user1, user2] }
 *     weeklyHistory:    {},  // "2026-04-14" → snapshot
 *     lastSnapshotWeek: null,
 *     actionItems:      {},  // "p0-g1" → [{ id, task, owner, dueDate, ... }]
 *     dms:              {},  // "Emily,Ricky" → { participants, messages }
 *     presence:         {},  // "Ricky" → { at, field }
 *   }
 *
 * Preservation rule: always read existing meta from server before saving,
 * merge your changes, then POST the full merged object. Otherwise you risk
 * clobbering fields owned by other surfaces (e.g. hsComments from the
 * HubSpot dashboard).
 */

export const DEFAULT_META = {
  fieldEdits: {},
  comments: [],
  weeklySummary: {},
  execSummary: null,
  aiPreferences: [],
  hsComments: [],
  reactions: {},
  weeklyHistory: {},
  lastSnapshotWeek: null,
  actionItems: {},
  dms: {},
  presence: {},
  teamUpdates: {}, // { gtm: { tldr, wins, flags, fyis, dri, savedAt, savedBy }, ... }
  discussFlags: {}, // { 'p0-g1': true } — resets every Wednesday wipe
  // Long-form Company Update Zoe ships every Monday. Distinct from the
  // exec summary slot. { markdown, generatedAt, generatedBy, edited? }
  // — null when no current update is published.
  companyUpdate: null,
  // Structured Quarterly Pre-read for Zoe's email + leadership Tuesday
  // meeting prep. { topLine, goalHealth[], highlights[], needsAttention[],
  // generatedAt, generatedBy, edited? } — null when no current pre-read.
  quarterlyPreread: null,
}

export async function fetchMetaData() {
  try {
    // `cache: 'no-store'` forces the browser to hit the network every
    // time, bypassing any HTTP cache. Without this, a recent save that
    // cleared a field could still be hidden by a cached response showing
    // the pre-save state.
    const res = await fetch(API_BASE + '/meta', { cache: 'no-store' })
    if (res.status === 204) return { ...DEFAULT_META }
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    // Merge with defaults so undefined keys don't break consumers
    return { ...DEFAULT_META, ...data }
  } catch (err) {
    console.warn('fetchMetaData: worker unreachable, using defaults', err)
    return { ...DEFAULT_META }
  }
}

export async function saveMetaData(meta) {
  const res = await fetch(API_BASE + '/meta', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(meta),
  })
  if (!res.ok) throw new Error(`Save failed: HTTP ${res.status}`)
  publishAblyEvent('meta-changed').catch(() => {})
  return meta
}

/**
 * Partial update: only update presence[userName] server-side, leave the
 * rest of meta untouched. Used by the heartbeat so frequent 20-second
 * writes can NEVER accidentally clobber a concurrent delete, edit, or
 * comment on another field.
 *
 * Safe to call every 20 seconds — the worker does the merge, not us.
 */
export async function savePresenceHeartbeat({ userName, at, pillar }) {
  if (!userName) return
  const res = await fetch(API_BASE + '/meta/presence', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userName, at, pillar }),
  })
  if (!res.ok) throw new Error(`Presence save failed: HTTP ${res.status}`)
}

/**
 * Archive a week's state (weeklySummary + execSummary + actionItems) into
 * meta.weeklyHistory[weekKey]. Idempotent on the server — won't overwrite
 * an existing snapshot for the same week.
 */
export async function createWeeklySnapshot(weekKey) {
  const res = await fetch(API_BASE + '/meta/snapshot', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ weekKey }),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Weekly snapshot failed: HTTP ${res.status} ${msg}`)
  }
  publishAblyEvent('meta-changed').catch(() => {})
  return res.json()
}

/**
 * Save (or clear) the executive summary slot — server-side partial update
 * so concurrent unrelated writers can't clobber it.
 *   payload = { tldr, flagsSummary, generatedAt, generatedBy, edited? }
 *   payload = { clear: true }                       // wipes the slot
 */
export async function saveExecSummary(payload) {
  const res = await fetch(API_BASE + '/meta/execSummary', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload || {}),
  })
  if (!res.ok) throw new Error(`Exec summary save failed: HTTP ${res.status}`)
  publishAblyEvent('meta-changed').catch(() => {})
  const data = await res.json()
  return data.execSummary
}

/**
 * Add or remove an AI writing preference — server-side partial update.
 *   op = 'create' + pref = { id, text, addedAt, addedBy }
 *   op = 'remove' + id
 * Returns the full post-mutation preferences list.
 */
export async function mutateAiPreferences({ op, pref, id }) {
  const res = await fetch(API_BASE + '/meta/aiPreferences', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ op, pref, id }),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`AI prefs ${op} failed: HTTP ${res.status} ${msg}`)
  }
  publishAblyEvent('meta-changed').catch(() => {})
  const data = await res.json()
  return data.aiPreferences
}

/**
 * Ask the worker to call Claude and generate a fresh exec summary.
 * Inputs:
 *   weeklySummaries  — primary: per-goal {goalName, tldr, wins, flags, dri, savedBy}
 *   subgoalUpdates   — secondary: per-subgoal {pillar, goalName, subgoalName,
 *                                              current, target, unit, update}
 *   teamUpdates      — primary: per-team {teamName, tldr, wins, flags, dri, savedBy}
 *   openActionItems  — tertiary: open action items for overdue detection
 *   aiPreferences    — writing-style rules
 *
 * Returns {
 *   tldr,
 *   winBullets:  [{ text, sourceType, sourceLabel, author }],
 *   flagBullets: [{ text, sourceType, sourceLabel, author }],
 *   winsSummary, flagsSummary   // legacy prose, derived from bullets
 * }.
 *
 * Does NOT persist the result — caller decides whether to save via
 * saveExecSummary() after reviewing.
 */
export async function generateExecSummary({
  weeklySummaries,
  subgoalUpdates,
  openActionItems,
  aiPreferences,
  teamUpdates,
}) {
  const res = await fetch(API_BASE + '/ai-summary', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      weeklySummaries,
      subgoalUpdates,
      openActionItems,
      aiPreferences,
      teamUpdates,
    }),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`AI generation failed: HTTP ${res.status} ${msg}`)
  }
  return res.json()
}

/**
 * Partial update for a single goal's action-items list. Operations are
 * applied server-side so concurrent unrelated writes (weekly summary
 * edits, comments on other goals) CANNOT resurrect a deleted item.
 *
 * The worker returns the full post-mutation list so we can reconcile
 * the cache with the authoritative server state.
 */
/**
 * Save a patch to one goal's weekly summary via the partial-update
 * endpoint. Only touches meta.weeklySummary[goalKey]; other fields
 * untouched. Any fields NOT included in `patch` are preserved from
 * the existing value server-side, so partial edits (e.g. only clearing
 * TLDR) don't wipe the untouched fields.
 */
export async function saveWeeklySummary({ goalKey, entry, patch }) {
  // Patch form is the default — only the field(s) that actually
  // changed go over the wire, and the server merges them onto the
  // current KV state. That way concurrent saves by other teammates
  // on other fields don't get overwritten by our stale draft values.
  //
  // `entry` form is kept as an escape hatch for call-sites that
  // genuinely need full-replace semantics (e.g. carry-forward into
  // an empty slot). Server handles both shapes.
  const res = await fetch(API_BASE + '/meta/weeklySummary', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ goalKey, ...(patch ? { patch } : { entry }) }),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Weekly summary save failed: HTTP ${res.status} ${msg}`)
  }
  const data = await res.json()
  // Broadcast the full authoritative entry (server returns it post-
  // merge) so other clients apply it directly to cache — no refetch.
  publishAblyEvent('weekly-summary-updated', {
    goalKey,
    entry: data.entry,
  }).catch(() => {})
  return data.entry
}

/**
 * Send a DM message via the server-side partial-update endpoint. Only
 * touches meta.dms[threadKey] — other slices untouched. Much faster and
 * safer than POST /meta (the full-blob write).
 *
 * After a successful save, broadcasts the full message payload over
 * Ably so receivers can insert it directly into their cache without
 * waiting for a meta refetch. The 1.5s delayed-invalidate that guards
 * KV eventual consistency is bypassed for DMs; real-time feels instant.
 */
export async function sendDmMessage({ threadKey, participants, message }) {
  const res = await fetch(API_BASE + '/meta/dms', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ op: 'send', threadKey, participants, message }),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`DM send failed: HTTP ${res.status} ${msg}`)
  }
  const data = await res.json()
  // Broadcast the full message payload so receivers can skip the refetch
  publishAblyEvent('dm', {
    threadKey,
    participants,
    message,
  }).catch(() => {})
  return data
}

/**
 * Toggle an emoji reaction for a given label (goal name or sub-goal
 * name). Server-side partial update — other meta fields untouched.
 * Returns the post-mutation reactions map for this label:
 *   { '👍': ['Ricky', 'Zoe'], '🎉': ['Katie'] }
 */
export async function toggleReaction({ label, emoji, user }) {
  const res = await fetch(API_BASE + '/meta/reactions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ op: 'toggle', label, emoji, user }),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Reaction toggle failed: HTTP ${res.status} ${msg}`)
  }
  publishAblyEvent('meta-changed').catch(() => {})
  const data = await res.json()
  return data.reactions || {}
}

/**
 * Save one team's weekly update via the partial-update endpoint. Only
 * touches meta.teamUpdates[teamId]. Other teams (and unrelated meta
 * slices) are untouched.
 *
 * Same full-entry-write pattern as weeklySummary: the client sends the
 * complete entry rather than a patch, so back-to-back saves don't hit
 * KV's 60-second read cache and resurrect cleared fields.
 *
 * Broadcasts the full authoritative entry over Ably so other clients
 * apply it to cache directly — no refetch, no KV propagation delay.
 */
export async function saveTeamUpdate({ teamId, entry, patch }) {
  // Same patch-preferred shape as saveWeeklySummary. See that
  // function for the concurrency rationale — TL;DR: partial-field
  // patches prevent stale drafts from overwriting teammates' fresh
  // saves on unrelated fields.
  const res = await fetch(API_BASE + '/meta/teamUpdates', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ teamId, ...(patch ? { patch } : { entry }) }),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Team update save failed: HTTP ${res.status} ${msg}`)
  }
  const data = await res.json()
  publishAblyEvent('team-update-updated', {
    teamId,
    entry: data.entry,
  }).catch(() => {})
  return data.entry
}

/**
 * Comments partial-update. Server applies the operation to the live
 * comments array and returns the full merged list. Concurrency-safe
 * vs. two teammates posting in different threads (the old full-meta
 * POST path could lose replies by clobbering with a stale snapshot).
 *
 * Ops:
 *   { op: 'createThread', thread }           — append
 *   { op: 'addReply', threadId, reply }      — append into one thread
 *   { op: 'setResolved', threadId, resolved, resolvedBy?, resolvedAt? }
 *   { op: 'deleteThread', threadId }         — remove
 */
export async function mutateComments(payload) {
  const res = await fetch(API_BASE + '/meta/comments', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload || {}),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Comments ${payload?.op || '(?)'} failed: HTTP ${res.status} ${msg}`)
  }
  const data = await res.json()
  publishAblyEvent('meta-changed').catch(() => {})
  return data.comments
}

/**
 * Save one fieldEdit attribution entry via the partial endpoint.
 * Server merges onto meta.fieldEdits without touching other keys.
 *
 *   key  = composite like "p0-g1-s2-update"
 *   edit = { by, at }
 */
export async function saveFieldEdit({ key, edit }) {
  const res = await fetch(API_BASE + '/meta/fieldEdits', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, edit }),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Field edit save failed: HTTP ${res.status} ${msg}`)
  }
  const data = await res.json()
  publishAblyEvent('meta-changed').catch(() => {})
  return data.edit
}

export async function mutateActionItems({ op, goalKey, item, itemId, patch }) {
  const res = await fetch(API_BASE + '/meta/actionItems', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ op, goalKey, item, itemId, patch }),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Action item ${op} failed: HTTP ${res.status} ${msg}`)
  }
  const data = await res.json()
  // Signal other clients to refetch. `meta-changed` matches the existing
  // channel name used by saveMetaData.
  publishAblyEvent('meta-changed').catch(() => {})
  return data.items
}

/**
 * Ask the worker to draft a team's TLDR/Wins/Flags from the goal +
 * sub-goal updates owned by that team's DRI. Caller does the
 * goal/subgoal owner matching client-side; this just synthesizes.
 *
 * Returns { tldr, wins, flags, generatedAt, inputs: { goalCount, subgoalCount } }.
 *
 * Does NOT persist — the caller writes the result into the team
 * update fields and the lead saves via the existing useSaveTeamUpdate
 * mutation.
 */
export async function generateTeamUpdate({ teamName, teamDri, goals, subgoals } = {}) {
  const res = await fetch(API_BASE + '/ai-team-update', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      teamName: teamName || '',
      teamDri: teamDri || '',
      goals: goals || [],
      subgoals: subgoals || [],
    }),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Team update generate failed: HTTP ${res.status} ${msg}`)
  }
  return res.json()
}

/**
 * Ask the worker to generate the structured Quarterly Pre-read.
 * Returns { topLine, goalHealth[], highlights[], needsAttention[],
 * generatedAt, model }. Does not persist — caller decides when to save.
 */
export async function generateQuarterlyPreread({
  weeklySummaries,
  teamUpdates,
  pillarHealth,
  bigPicture,
} = {}) {
  const res = await fetch(API_BASE + '/ai-quarterly-preread', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      weeklySummaries: weeklySummaries || [],
      teamUpdates: teamUpdates || [],
      pillarHealth: pillarHealth || [],
      bigPicture: bigPicture || {},
    }),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Pre-read generate failed: HTTP ${res.status} ${msg}`)
  }
  return res.json()
}

/**
 * Save (or clear) the Quarterly Pre-read slot. Leadership-gated.
 *   payload = { topLine, goalHealth, highlights, needsAttention,
 *               generatedAt, generatedBy, edited? }
 *   payload = { clear: true }
 */
export async function saveQuarterlyPreread(payload) {
  const res = await fetch(API_BASE + '/meta/quarterlyPreread', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload || {}),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Pre-read save failed: HTTP ${res.status} ${msg}`)
  }
  publishAblyEvent('meta-changed').catch(() => {})
  const data = await res.json()
  return data.quarterlyPreread
}

/**
 * Save (or clear) the long-form Company Update slot — server-side
 * partial update. Leadership-gated on the worker (Zoe + Ricky).
 *   payload = { markdown, generatedAt, generatedBy, edited? }
 *   payload = { clear: true }
 */
export async function saveCompanyUpdate(payload) {
  const res = await fetch(API_BASE + '/meta/companyUpdate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload || {}),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Company update save failed: HTTP ${res.status} ${msg}`)
  }
  publishAblyEvent('meta-changed').catch(() => {})
  const data = await res.json()
  return data.companyUpdate
}

/**
 * Ask the worker to generate a fresh Company Update markdown draft via
 * Anthropic. Sends team updates (with FYIs), goal weekly summaries,
 * sub-goal updates, and big-picture metrics; the worker pulls past 4
 * Company Updates from snapshots as tone reference.
 *
 * Returns { markdown, generatedAt, model, toneRefs }.
 *
 * Does NOT persist the result — caller decides when to save.
 */
export async function generateCompanyUpdate({
  weeklySummaries,
  subgoalUpdates,
  teamUpdates,
  bigPicture,
  orphanGoals,
  eoBuyoutsContent,
} = {}) {
  const res = await fetch(API_BASE + '/ai-company-update', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      weeklySummaries: weeklySummaries || [],
      subgoalUpdates: subgoalUpdates || [],
      teamUpdates: teamUpdates || [],
      bigPicture: bigPicture || {},
      // Goals Zoe explicitly chose to include from the orphan picker.
      // Empty array = no extras (the default first-generation case).
      orphanGoals: orphanGoals || [],
      // EO Buyouts pillar content for the Upcoming Transactions section.
      // null when the pillar isn't found (legacy clients, archive view).
      eoBuyoutsContent: eoBuyoutsContent || null,
    }),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Company update generate failed: HTTP ${res.status} ${msg}`)
  }
  return res.json()
}

/**
 * Toggle the "discuss this week" flag on a goal. Server stores
 * meta.discussFlags = { [goalKey]: true } — falsy values are deleted
 * from the map. Resets every Wednesday wipe.
 *
 *   setDiscussFlag({ goalKey: 'p0-g1', value: true })
 *   setDiscussFlag({ goalKey: 'p0-g1', value: false })
 */
export async function setDiscussFlag({ goalKey, value }) {
  const res = await fetch(API_BASE + '/meta/discussFlags', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ goalKey, value: Boolean(value) }),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Discuss flag save failed: HTTP ${res.status} ${msg}`)
  }
  const data = await res.json()
  publishAblyEvent('meta-changed').catch(() => {})
  return data.discussFlags || {}
}
